import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Tabs } from '@/shared/ui';

import styles from './AdminLayout.module.css';

interface AdminTab {
  path: string;
  labelKey: string;
  ownerOnly?: boolean;
}

interface AdminGroup {
  id: string;
  labelKey: string;
  items: AdminTab[];
}

/**
 * Навигация админки — два уровня: группа, затем экран внутри группы.
 *
 * Раньше все 14 экранов лежали одним рядом: он уезжал в горизонтальный скролл,
 * и найти нужный экран можно было только перебором. Группы разбиты по частоте
 * и роду работы, а не по алфавиту: «Операции» — то, что открывают каждый день,
 * дальше разбор, деньги, контент и настройки.
 *
 * Маршруты НЕ меняются: это тот же список путей, только разложенный по группам.
 * Любая закладка и любая прямая ссылка продолжают работать.
 */
const GROUPS: AdminGroup[] = [
  {
    id: 'ops',
    labelKey: 'admin.nav.ops',
    items: [
      { path: '/admin', labelKey: 'admin.dashboard.title' },
      { path: '/admin/process', labelKey: 'admin.process.title' },
      { path: '/admin/withdrawals', labelKey: 'admin.withdrawals.title' },
      { path: '/admin/deposits', labelKey: 'admin.deposits.title' },
      { path: '/admin/level-requests', labelKey: 'admin.level_requests.title' },
      { path: '/admin/clients', labelKey: 'admin.clients.title' },
    ],
  },
  {
    id: 'insight',
    labelKey: 'admin.nav.insight',
    items: [
      { path: '/admin/analytics', labelKey: 'admin.analytics.title' },
      { path: '/admin/rounds-history', labelKey: 'admin.rounds.title' },
      { path: '/admin/referrals', labelKey: 'admin.referrals.title' },
      { path: '/admin/audit', labelKey: 'admin.audit.title' },
    ],
  },
  {
    id: 'money',
    labelKey: 'admin.nav.money',
    items: [
      { path: '/admin/core', labelKey: 'admin.core.title' },
      { path: '/admin/adjustments', labelKey: 'adjustments.title' },
    ],
  },
  {
    id: 'content',
    labelKey: 'admin.nav.content',
    items: [
      { path: '/admin/broadcast', labelKey: 'admin.broadcast.title' },
      { path: '/admin/faq', labelKey: 'admin.faq.title' },
    ],
  },
  {
    id: 'system',
    labelKey: 'admin.nav.system',
    items: [
      { path: '/admin/settings', labelKey: 'admin.settings.title' },
      { path: '/admin/staff', labelKey: 'admin.staff.title' },
    ],
  },
];

export function AdminLayout(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Все группы доступны всем staff (admin и owner)
  const groups = GROUPS;

  /**
   * Активный экран — самый длинный совпадающий префикс: /admin/clients/42
   * подсвечивает «Клиенты», а не «Сводку». Логика сохранена от прежней версии.
   */
  const activePath =
    groups
      .flatMap((group) => group.items)
      .filter(
        (item) =>
          location.pathname === item.path || location.pathname.startsWith(`${item.path}/`),
      )
      .sort((a, b) => b.path.length - a.path.length)[0]?.path ?? '/admin';

  const activeGroup =
    groups.find((group) => group.items.some((item) => item.path === activePath)) ?? groups[0];

  const groupItems = activeGroup?.items ?? [];

  return (
    <div className={styles.layout}>
      <div className={styles.navWrap}>
        <div className={styles.navBar}>
          {/* Выход в клиентский кабинет — отдельная кнопка, а не вкладка среди
              вкладок: у управляющего свой капитал и свои кольца, он такой же
              участник. Раньше «Поле» стояло первым в общем ряду и читалось как
              раздел админки, хотя уводит из неё. */}
          <button
            type="button"
            className={styles.exitButton}
            onClick={() => navigate('/')}
            title={t('admin.nav.exit_hint')}
          >
            <span aria-hidden="true">←</span> {t('nav.field')}
          </button>

          <Tabs
            aria-label={t('admin.nav.groups')}
            value={activeGroup?.id ?? ''}
            onChange={(groupId) => {
              // Переход в группу открывает её первый экран: вкладка группы сама
              // по себе не имеет содержимого.
              const target = groups.find((group) => group.id === groupId);
              if (target?.items[0]) navigate(target.items[0].path);
            }}
            items={groups.map((group) => ({ value: group.id, label: t(group.labelKey) }))}
            className={styles.groupTabs}
          />
        </div>

        {/* Второй уровень скрываем, когда в группе один экран — ряд из одной
            вкладки только занимает место. */}
        {groupItems.length > 1 && (
          <div className={styles.tabsRow}>
            <Tabs
              aria-label={activeGroup ? t(activeGroup.labelKey) : t('admin.title')}
              value={activePath}
              onChange={(path) => navigate(path)}
              items={groupItems.map((item) => ({ value: item.path, label: t(item.labelKey) }))}
            />
          </div>
        )}
      </div>

      <div className={styles.body}>
        <Outlet />
      </div>
    </div>
  );
}
